import { ConfirmationAction } from '@app/components/ConfirmationAction';
import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import { adminAuditPaths } from '@app/modules/audit/routing/admin-audit-paths';
import {
  auditReportsListReportDefinitionQueryKey,
  useAuditReportsDeleteReportDefinition,
} from '@mi-front/api-v2';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDefinitionContext } from '../../contexts/DefinitionContext';

export function DeleteButton() {
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const { initialDefinition } = useDefinitionContext();
  const queryClient = useQueryClient();

  const deleteMutation = useAuditReportsDeleteReportDefinition({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: auditReportsListReportDefinitionQueryKey({
            reportType: initialDefinition.reportType,
          }),
        });
        notifySuccess(
          $t({
            id: 'audit.reportDefinitions.deleteSuccess',
            defaultMessage: 'Plantilla eliminada exitosamente',
          }),
        );
        pushTo(adminAuditPaths.reportDefinitions(initialDefinition.reportType));
      },
      onError: (error) => {
        notifyError(
          $t({
            id: 'audit.reportDefinitions.deleteError',
            defaultMessage: 'Error al eliminar la plantilla',
          }),
        );
        console.error(error);
      },
    },
  });

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ id: initialDefinition.id });
  };

  const isDisabled = initialDefinition.isDefault;

  return (
    <ConfirmationAction
      title={$t({
        id: 'audit.reportDefinitions.deleteTemplateTitle',
        defaultMessage: 'Eliminar plantilla',
      })}
      message={
        <FormattedMessage
          id="audit.reportDefinitions.deleteTemplateConfirm"
          defaultMessage={
            '¿Está seguro de que desea eliminar la plantilla "{name}"? Esta acción no se puede deshacer.'
          }
          values={{ name: initialDefinition.name }}
        />
      }
      confirmLabel={$t({
        id: 'common.delete',
        defaultMessage: 'Eliminar',
      })}
      cancelLabel={$t({
        id: 'common.cancel',
        defaultMessage: 'Cancelar',
      })}
      confirmColor="error"
      onConfirm={handleDelete}
      onError={(error) => {
        console.error(error);
      }}
    >
      {({ onClick }) => (
        <Tooltip
          title={
            isDisabled
              ? $t({
                  id: 'audit.reportDefinitions.cannotDeleteDefault',
                  defaultMessage: 'No se puede eliminar la plantilla default',
                })
              : ''
          }
        >
          <span>
            <Button
              onClick={onClick}
              disabled={isDisabled}
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
            >
              <FormattedMessage id="common.delete" defaultMessage="Eliminar" />
            </Button>
          </span>
        </Tooltip>
      )}
    </ConfirmationAction>
  );
}
